/**
 * SISTEMA DE INTERNACIONALIZACIÓN (i18n)
 * 
 * Este contexto maneja todo el sistema bilingüe de la aplicación (ES/EN).
 * Incluye traducciones completas, cambio dinámico de idioma y selector de idioma.
 * 
 * ESTRUCTURA DE TRADUCCIONES:
 * - Objeto anidado con secciones: nav, landing, profile, experience, etc.
 * - Claves descriptivas: nav.perfil, experience.title, projects.webSection.title
 * 
 * REGLA CRÍTICA: Siempre añadir traducciones para AMBOS idiomas (ES/EN)
 * cuando se agregue nuevo texto a la aplicación.
 * 
 * COMPONENTES PRINCIPALES:
 * - LanguageProvider: Provider del contexto
 * - LanguageSelector: Componente Menu para cambio de idioma
 * - Función t(): Helper para traducir claves
 */

import React, { createContext, useContext, useState } from "react";
import { 
  Menu, 
  MenuButton, 
  MenuList, 
  MenuItem, 
  Button, 
  Text 
} from '@chakra-ui/react';

// Crear el contexto de idioma
const LanguageContext = createContext();

/**
 * OBJETO DE TRADUCCIONES
 * 
 * Estructura anidada que contiene todas las traducciones de la aplicación.
 * Cada sección tiene su propio objeto para mejor organización.
 * 
 * PARA AÑADIR NUEVAS TRADUCCIONES:
 * 1. Añadir en es: { seccion: { clave: "texto español" } }
 * 2. Añadir en en: { seccion: { clave: "english text" } }
 * 3. Usar en componente: t('seccion.clave')
 */
const translations = {
  es: {
    // Navegación principal
    nav: {
      inicio: "Inicio",
      perfil: "Perfil",
      experiencia: "Experiencia",
      proyectos: "Proyectos",
      educacion: "Educación",
      contacto: "Contacto"
    },
    // Landing
    landing: {
      title: "Desarrollador Web & IoT",
      subtitle: "Especialista en desarrollo web moderno y soluciones IoT",
      cta: "Ver mi trabajo"
    },
    // Profile
    profile: {
      title: "Sobre mí",
      description: "Desarrollador Full Stack especializado en desarrollo web e IoT, con sólida formación en Ingeniería Eléctrica. Mi enfoque se centra en crear soluciones tecnológicas integrales que combinan desarrollo de software, automatización y sistemas embebidos. Experiencia en arquitecturas cloud, DevOps y tecnologías emergentes como IA aplicada a soluciones empresariales. Implemento herramientas como N8n para la creación de agentes inteligentes y automatización de procesos empresariales.",
      skills: "Tecnologías"
    },
    // Experience
    experience: {
      title: "Experiencia Laboral",
      technologies: "Tecnologías utilizadas:",
      achievements: "Logros principales:",
      present: "Presente",
      // Job titles and descriptions
      fullStackDev: "Desarrollador Full Stack",
      erpDev: "Desarrollador Full Stack - Sistema ERP",
      aiSpecialist: "Especialista en Automatización e IA",
      // Company descriptions
      anonymousPcDesc: "Desarrollo completo de e-commerce para tienda de equipos informáticos. Implementación de sistema de inventario, gestión de pedidos, pasarelas de pago y panel administrativo.",
      erpDesc: "Desarrollo de sistema ERP personalizado para la gestión integral del negocio Anonymous PC. Control de inventario, ventas, proveedores y reportes analíticos. Implementación de agentes inteligentes con N8n para automatización de procesos empresariales.",
      aiDesc: "Implementación de soluciones de automatización empresarial utilizando N8n para la creación de agentes inteligentes. Desarrollo de chatbots con RAG, automatización de procesos de negocio y integración de APIs para workflow optimization.",
      // Location and type
      freelance: "Freelance",
      ecommerce: "E-commerce",
      management: "Sistema de Gestión",
      automation: "Automatización",
      diverseProjects: "Proyectos Diversos"
    },
    // Projects
    projects: {
      title: "Proyectos",
      viewProject: "Ver Proyecto",
      technologies: "Tecnologías:",
      functionality: "Funcionamiento:",
      applications: "Aplicaciones:",
      close: "Cerrar",
      webSection: {
        title: "💻 Proyectos Web",
        subtitle: "Aplicaciones web completas con tecnologías modernas"
      },
      iotSection: {
        title: "🔌 Proyectos IoT",
        subtitle: "Dispositivos conectados y sistemas embebidos"
      },
      webProjects: {
        anonymouspc: {
          title: "Anonymous PC",
          description: 'E-commerce desarrollado para la tienda de equipos informáticos "Anonymous PC". Incluye sistema de catálogo, carrito de compras y gestión de inventario.'
        },
        dashboard: {
          title: "Dashboard Empresarial",
          description: "Sistema de gestión empresarial con módulos ERP/CRM. Incluye análisis de datos, reportes y automatización de procesos con N8n."
        },
        chatbot: {
          title: "ChatLVT - Asistente Virtual Universitario",
          description: "Chatbot inteligente alimentado por LLM de código abierto que funciona como asistente virtual para la UTELVT. Implementa RAG (Retrieval Augmented Generation) con LangChain para proporcionar información relevante sobre la universidad, mejorando la atención estudiantil y demostrando el potencial de los sistemas de IA en educación."
        }
      },
      iotProjects: {
        smartLighting: {
          title: "🧠 Iluminación Inteligente Automatizada",
          description: "Sistema de control de iluminación automatizado que regula el encendido de luces mediante sensores ambientales (LDR) y de movimiento (PIR), gestionado por un ESP32.",
          fullDescription: {
            overview: "Sistema de control de iluminación automatizado que regula el encendido de luces mediante sensores ambientales (LDR) y de movimiento (PIR), gestionado por un ESP32.",
            functionality: [
              "El sensor LDR mide la luminosidad ambiente.",
              "El sensor PIR detecta movimiento humano.",
              "Si hay baja luz y se detecta movimiento, el ESP32 activa un relé para encender las luces.",
              "Puede integrarse con asistentes como Alexa o Google Home mediante APIs."
            ],
            applications: [
              "Ahorro energético en espacios públicos.",
              "Automatización en corredores y oficinas inteligentes."
            ]
          }
        },
        accessControl: {
          title: "🔐 Sistema de Acceso con Huella y RFID",
          description: "Sistema biométrico de control de acceso con doble autenticación mediante huella dactilar y tarjetas RFID.",
          fullDescription: {
            overview: "Sistema biométrico de control de acceso con doble autenticación mediante huella dactilar y tarjetas RFID.",
            functionality: [
              "El sensor R307 escanea huellas y las compara con una base de datos.",
              "El lector RC522 verifica tarjetas RFID como método secundario.",
              "El ESP32 desbloquea un electroimán si la autenticación es exitosa.",
              "Registro de accesos en Google Sheets o servidor local."
            ],
            applications: [
              "Oficinas, laboratorios, salas de servidores.",
              "Control de acceso en instituciones educativas."
            ]
          }
        },
        surgeProtector: {
          title: "🔌 Protector de Sobretensión Automático",
          description: "Sistema de protección contra sobretensiones que actúa desconectando automáticamente el suministro al detectar valores peligrosos.",
          fullDescription: {
            overview: "Sistema de protección contra sobretensiones que actúa desconectando automáticamente el suministro al detectar valores peligrosos.",
            functionality: [
              "El sensor ZMPT101B mide voltaje en tiempo real.",
              "Si el voltaje supera los 250V, el ESP32 activa un relé para cortar la energía.",
              "La conexión se restablece cuando el voltaje vuelve a la normalidad.",
              "Guarda eventos en memoria EEPROM."
            ],
            applications: [
              "Protección de equipos sensibles en zonas con inestabilidad eléctrica."
            ]
          }
        },
        solarCharger: {
          title: "☀️ Cargador Solar Inteligente con Seguimiento",
          description: "Sistema de carga solar de baja potencia con seguimiento solar básico y protección electrónica.",
          fullDescription: {
            overview: "Sistema de carga solar de baja potencia con seguimiento solar básico y protección electrónica.",
            functionality: [
              "LDRs miden la posición óptima del sol para orientar los paneles.",
              "El ESP32 controla la carga de la batería (modo CC/CV).",
              "Protección contra sobrecargas y monitoreo de voltaje/corriente.",
              "Puede mostrar datos o alertas por interfaz web o LCD."
            ],
            applications: [
              "Sistemas solares domésticos.",
              "Iluminación solar inteligente."
            ]
          }
        },
        powerFactorCalculator: {
          title: "📏 Calculadora de Factor de Potencia y Corrección",
          description: "Dispositivo que mide el factor de potencia de una carga y recomienda la capacitancia adecuada para su corrección.",
          fullDescription: {
            overview: "Dispositivo que mide el factor de potencia de una carga y recomienda la capacitancia adecuada para su corrección.",
            functionality: [
              "El ACS712 y el ZMPT101B miden corriente y voltaje.",
              "El ESP32 calcula el desfase usando cruce por cero.",
              "Muestra el factor de potencia (cos(φ)) y el valor del capacitor recomendado.",
              "Interfaz con pantalla LCD 16x2."
            ],
            applications: [
              "Optimización energética en sistemas con motores u otras cargas inductivas."
            ]
          }
        },
        prepaidEnergyManager: {
          title: "⚡ Gestor de Energía Prepago",
          description: "Dispositivo inteligente que permite monitorear y gestionar el consumo eléctrico mediante un sistema prepago, ideal para aplicaciones donde se requiere control individual de consumo energético.",
          fullDescription: {
            overview: "Dispositivo inteligente que permite monitorear y gestionar el consumo eléctrico mediante un sistema prepago, ideal para aplicaciones donde se requiere control individual de consumo energético.",
            functionality: [
              "El sensor HLW8012 o ZMPT101B mide el consumo eléctrico en tiempo real (kWh).",
              "El lector RC522 permite recargar crédito utilizando tarjetas RFID.",
              "El ESP32 controla el flujo de energía: cuando el saldo llega a cero, se activa un relé que interrumpe el suministro eléctrico.",
              "El sistema calcula y descuenta automáticamente el consumo del saldo cargado.",
              "Pantalla LCD para mostrar saldo disponible y consumo acumulado.",
              "Conectividad Wi-Fi para gestión remota, monitoreo y visualización de datos en la nube.",
              "Almacenamiento de registros históricos de consumo."
            ],
            applications: [
              "Gestión energética individual en edificios de apartamentos.",
              "Soluciones de bajo costo para comunidades con acceso limitado a la energía.",
              "Proyectos educativos de control y eficiencia energética."
            ]
          }
        }
      }
    },
    // Education
    education: {
      title: "Educación y Certificaciones",
      skills: "Habilidades:",
      verify: "Verificar Certificado",
      reflection: "El aprendizaje continuo es la clave para mantenerse relevante en un mundo tecnológico en constante evolución."
    },
    // Contact
    contact: {
      title: "Información de Contacto",
      mainTitle: "Hablemos",
      subtitle: "¿Tienes un proyecto en mente? Me encantaría escuchar tus ideas y ayudarte a convertirlas en realidad.",
      followMe: "Sígueme en:",
      name: "Nombre completo",
      email: "Correo electrónico",
      message: "Mensaje",
      send: "Enviar mensaje",
      required: "Requerido",
      invalidEmail: "Email inválido",
      formInstructions: "Completa el formulario y te responderé lo antes posible.",
      successMessage: "¡Mensaje enviado exitosamente!",
      successDescription: "Gracias por contactarme. Te responderé lo antes posible."
    },
    // Virtual Assistant
    assistant: {
      greeting: "¡Hola! Soy el asistente virtual de Henry. Puedes preguntarme sobre sus habilidades técnicas, experiencia profesional, proyectos o cualquier información del portafolio. ¿En qué puedo ayudarte?",
      placeholder: "Escribe tu pregunta aquí...",
      send: "Enviar"
    }
  },
  en: {
    // Navigation
    nav: {
      inicio: "Home",
      perfil: "Profile",
      experiencia: "Experience",
      proyectos: "Projects",
      educacion: "Education",
      contacto: "Contact"
    },
    // Landing
    landing: {
      title: "Web & IoT Developer",
      subtitle: "Specialist in modern web development and IoT solutions",
      cta: "View my work"
    },
    // Profile
    profile: {
      title: "About me",
      description: "Full Stack Developer specialized in web development and IoT, with solid training in Electrical Engineering. My focus is on creating comprehensive technological solutions that combine software development, automation and embedded systems. Experience in cloud architectures, DevOps and emerging technologies such as AI applied to business solutions. I implement tools like N8n for creating intelligent agents and automating business processes.",
      skills: "Technologies"
    },
    // Experience
    experience: {
      title: "Work Experience",
      technologies: "Technologies used:",
      achievements: "Key achievements:",
      present: "Present",
      // Job titles and descriptions
      fullStackDev: "Full Stack Developer",
      erpDev: "Full Stack Developer - ERP System",
      aiSpecialist: "Automation & AI Specialist",
      // Company descriptions
      anonymousPcDesc: "Complete e-commerce development for computer equipment store. Implementation of inventory system, order management, payment gateways and administrative panel.",
      erpDesc: "Development of custom ERP system for comprehensive Anonymous PC business management. Inventory control, sales, suppliers and analytical reports. Implementation of intelligent agents with N8n for business process automation.",
      aiDesc: "Implementation of enterprise automation solutions using N8n for creating intelligent agents. Development of RAG chatbots, business process automation and API integration for workflow optimization.",
      // Location and type
      freelance: "Freelance",
      ecommerce: "E-commerce",
      management: "Management System",
      automation: "Automation",
      diverseProjects: "Diverse Projects"
    },
    // Projects
    projects: {
      title: "Projects",
      viewProject: "View Project",
      technologies: "Technologies:",
      functionality: "Functionality:",
      applications: "Applications:",
      close: "Close",
      webSection: {
        title: "💻 Web Projects",
        subtitle: "Complete web applications with modern technologies"
      },
      iotSection: {
        title: "🔌 IoT Projects",
        subtitle: "Connected devices and embedded systems"
      },
      webProjects: {
        anonymouspc: {
          title: "Anonymous PC",
          description: 'E-commerce developed for "Anonymous PC" computer equipment store. Includes catalog system, shopping cart and inventory management.'
        },
        dashboard: {
          title: "Business Dashboard",
          description: "Business management system with ERP/CRM modules. Includes data analysis, reports and process automation with N8n."
        },
        chatbot: {
          title: "ChatLVT - University Virtual Assistant",
          description: "Intelligent chatbot powered by open source LLM that works as a virtual assistant for UTELVT. Implements RAG (Retrieval Augmented Generation) with LangChain to provide relevant information about the university, improving student service and demonstrating the potential of AI systems in education."
        }
      },
      iotProjects: {
        smartLighting: {
          title: "🧠 Smart Automated Lighting",
          description: "Automated lighting control system that regulates light activation through environmental sensors (LDR) and motion sensors (PIR), managed by an ESP32.",
          fullDescription: {
            overview: "Automated lighting control system that regulates light activation through environmental sensors (LDR) and motion sensors (PIR), managed by an ESP32.",
            functionality: [
              "The LDR sensor measures ambient brightness.",
              "The PIR sensor detects human movement.",
              "If there is low light and movement is detected, the ESP32 activates a relay to turn on the lights.",
              "Can be integrated with assistants like Alexa or Google Home through APIs."
            ],
            applications: [
              "Energy saving in public spaces.",
              "Automation in corridors and smart offices."
            ]
          }
        },
        accessControl: {
          title: "🔐 Fingerprint and RFID Access System",
          description: "Biometric access control system with dual authentication using fingerprint and RFID cards.",
          fullDescription: {
            overview: "Biometric access control system with dual authentication using fingerprint and RFID cards.",
            functionality: [
              "The R307 sensor scans fingerprints and compares them with a database.",
              "The RC522 reader verifies RFID cards as a secondary method.",
              "The ESP32 unlocks an electromagnet if authentication is successful.",
              "Access logging to Google Sheets or local server."
            ],
            applications: [
              "Offices, laboratories, server rooms.",
              "Access control in educational institutions."
            ]
          }
        },
        surgeProtector: {
          title: "🔌 Automatic Surge Protector",
          description: "Surge protection system that acts by automatically disconnecting the power supply when detecting dangerous voltage values.",
          fullDescription: {
            overview: "Surge protection system that acts by automatically disconnecting the power supply when detecting dangerous voltage values.",
            functionality: [
              "The ZMPT101B sensor measures voltage in real time.",
              "If voltage exceeds 250V, the ESP32 activates a relay to cut power.",
              "Connection is restored when voltage returns to normal.",
              "Saves events in EEPROM memory."
            ],
            applications: [
              "Protection of sensitive equipment in areas with electrical instability."
            ]
          }
        },
        solarCharger: {
          title: "☀️ Smart Solar Charger with Tracking",
          description: "Low-power solar charging system with basic solar tracking and electronic protection.",
          fullDescription: {
            overview: "Low-power solar charging system with basic solar tracking and electronic protection.",
            functionality: [
              "LDRs measure optimal sun position to orient the panels.",
              "The ESP32 controls battery charging (CC/CV mode).",
              "Overload protection and voltage/current monitoring.",
              "Can display data or alerts via web interface or LCD."
            ],
            applications: [
              "Home solar systems.",
              "Smart solar lighting."
            ]
          }
        },
        powerFactorCalculator: {
          title: "📏 Power Factor Calculator and Correction",
          description: "Device that measures the power factor of a load and recommends the appropriate capacitance for its correction.",
          fullDescription: {
            overview: "Device that measures the power factor of a load and recommends the appropriate capacitance for its correction.",
            functionality: [
              "The ACS712 and ZMPT101B measure current and voltage.",
              "The ESP32 calculates phase shift using zero crossing.",
              "Shows power factor (cos(φ)) and recommended capacitor value.",
              "Interface with 16x2 LCD display."
            ],
            applications: [
              "Energy optimization in systems with motors or other inductive loads."
            ]
          }
        },
        prepaidEnergyManager: {
          title: "⚡ Prepaid Energy Manager",
          description: "Smart device that allows monitoring and managing electrical consumption through a prepaid system, ideal for applications where individual energy consumption control is required.",
          fullDescription: {
            overview: "Smart device that allows monitoring and managing electrical consumption through a prepaid system, ideal for applications where individual energy consumption control is required.",
            functionality: [
              "The HLW8012 or ZMPT101B sensor measures electrical consumption in real time (kWh).",
              "The RC522 reader allows credit recharge using RFID cards.",
              "The ESP32 controls energy flow: when balance reaches zero, a relay is activated to interrupt the electrical supply.",
              "The system automatically calculates and deducts consumption from the loaded balance.",
              "LCD display to show available balance and accumulated consumption.",
              "Wi-Fi connectivity for remote management, monitoring and cloud data visualization.",
              "Storage of historical consumption records."
            ],
            applications: [
              "Individual energy management in apartment buildings.",
              "Low-cost solutions for communities with limited energy access.",
              "Educational projects on control and energy efficiency."
            ]
          }
        }
      }
    },
    // Education
    education: {
      title: "Education & Certifications",
      skills: "Skills:",
      verify: "Verify Certificate",
      reflection: "Continuous learning is the key to staying relevant in a constantly evolving technological world."
    },
    // Contact
    contact: {
      title: "Contact Information",
      mainTitle: "Let's Talk",
      subtitle: "Do you have a project in mind? I would love to hear your ideas and help you turn them into reality.",
      followMe: "Follow me on:",
      name: "Full name",
      email: "Email address",
      message: "Message",
      send: "Send message",
      required: "Required",
      invalidEmail: "Invalid email",
      formInstructions: "Complete the form and I will respond to you as soon as possible.",
      successMessage: "Message sent successfully!",
      successDescription: "Thank you for contacting me. I will respond to you as soon as possible."
    },
    // Virtual Assistant
    assistant: {
      greeting: "Hello! I'm Henry's virtual assistant. You can ask me about his technical skills, professional experience, projects or any portfolio information. How can I help you?",
      placeholder: "Type your question here...",
      send: "Send"
    }
  }
};

/**
 * PROVIDER DEL CONTEXTO DE IDIOMA
 * 
 * Maneja el estado del idioma actual y proporciona funciones para:
 * - toggleLanguage(): Cambiar entre ES/EN
 * - t(): Función helper para traducir claves
 * - language: Idioma actual ('es' o 'en')
 * 
 * INICIALIZACIÓN: Por defecto en español ('es')
 */
const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('es');

  // Función para alternar entre idiomas
  const toggleLanguage = () => {
    setLanguage(language === 'es' ? 'en' : 'es');
  };

  /**
   * Función helper para traducir claves
   * @param {string} key - Clave de traducción (ej: 'nav.inicio')
   * @returns {string} - Texto traducido o la clave si no existe
   */
  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    
    // Navegar por el objeto anidado usando las claves
    for (const k of keys) {
      value = value?.[k];
    }
    
    // Retornar traducción o clave original si no existe
    return value || key;
  };

  return (
    <LanguageContext.Provider value={{ language, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageProvider;
export { LanguageContext };

/**
 * COMPONENTE SELECTOR DE IDIOMA
 * 
 * Componente Menu de Chakra UI que permite cambiar entre idiomas.
 * Se muestra en el header de la aplicación.
 * 
 * CARACTERÍSTICAS:
 * - Menu desplegable con opciones ES/EN
 * - Indica idioma actual visualmente
 * - Previene clic en idioma ya seleccionado
 * - Estilos consistentes con el tema de la aplicación
 */
export const LanguageSelector = () => {
  const { language, toggleLanguage } = useContext(LanguageContext);

  // Configuración de opciones de idioma
  const languageOptions = {
    es: { label: 'ES' },
    en: { label: 'EN' }
  };

  return (
    <Menu>
      <MenuButton 
        as={Button}
        rightIcon="▼"
        variant="solid"
        bg="blue.600"
        color="white"
        size="sm"
        _hover={{ bg: 'blue.700' }}
        _active={{ bg: 'blue.800' }}
        fontWeight="bold"
        borderRadius="md"
        minW="60px"
      >
        <Text fontSize="sm">{languageOptions[language].label}</Text>
      </MenuButton>
      <MenuList borderColor="blue.200" boxShadow="lg">
        {/* Opción Español */}
        <MenuItem 
          onClick={() => language !== 'es' && toggleLanguage()}
          bg={language === 'es' ? 'blue.100' : 'transparent'}
          color={language === 'es' ? 'blue.800' : 'gray.700'}
          _hover={{ bg: 'blue.50' }}
          fontWeight={language === 'es' ? 'bold' : 'normal'}
        >
          <Text>ES - Español</Text>
        </MenuItem>
        {/* Opción Inglés */}
        <MenuItem 
          onClick={() => language !== 'en' && toggleLanguage()}
          bg={language === 'en' ? 'blue.100' : 'transparent'}
          color={language === 'en' ? 'blue.800' : 'gray.700'}
          _hover={{ bg: 'blue.50' }}
          fontWeight={language === 'en' ? 'bold' : 'normal'}
        >
          <Text>EN - English</Text>
        </MenuItem>
      </MenuList>
    </Menu>
  );
};
